import type { ThemeAssetEntry } from '@goodtime/engine';
import { worldOf } from '@goodtime/shared';
import plateGoldUrl from '../assets/ui_button/plate_gold.png';
import plateWoodUrl from '../assets/ui_button/plate_wood.png';
import wheelHubUrl from '../assets/wheel/hub.png';
import wheelPointerUrl from '../assets/wheel/pointer.png';
import wheelRimUrl from '../assets/wheel/outer_rim.png';
import bombUrl from '../assets/item/bomb.png';
import cupsCoinsUrl from '../assets/minigame/cups/coins.png';
import lightBezelUrl from '../assets/road/light_bezel.png';

const GAME_ART = import.meta.glob<{ default: string }>('../assets/minigame/*/*.png', { eager: true });
const WORLD_GAME_ART = import.meta.glob<{ default: string }>('../assets/minigame/*/*/*.png', { eager: true });

/**
 * The road's mini-games' art (assets/minigame/<kind>/*.png), by play kind,
 * as `road:<kind>_<name>`. Fetched when a game first opens, while its title
 * card plays, so nobody downloads a game they haven't played.
 */
export const ROAD_GAMES: Record<string, Record<string, ThemeAssetEntry>> = {};

/**
 * Each game's lamp icon (assets/minigame/<kind>/icon.png), as
 * `road:game_<kind>`: the panel's lamps show them, so they load with the
 * road.
 */
export const ROAD_GAME_ICONS: Record<string, ThemeAssetEntry> = {};

for (const [file, mod] of Object.entries(GAME_ART)) {
  const match = /minigame\/([^/]+)\/([^/]+)\.png$/.exec(file);
  if (!match) continue;
  const [, kind, name] = match as unknown as [string, string, string];
  const path = `minigame/${kind}/${name}.png`;
  if (name === 'icon') {
    ROAD_GAME_ICONS[`game_${kind}`] = { alias: `road:game_${kind}`, src: mod.default, path };
  } else {
    (ROAD_GAMES[kind] ??= {})[name] = { alias: `road:${kind}_${name}`, src: mod.default, path };
  }
}

// The wheel game borrows the wheel room's rim, hub and pointer.
ROAD_GAMES.wheel = {
  ...ROAD_GAMES.wheel,
  rim: { alias: 'road:wheel_rim', src: wheelRimUrl, path: 'wheel/outer_rim.png' },
  hub: { alias: 'road:wheel_hub', src: wheelHubUrl, path: 'wheel/hub.png' },
  pointer: { alias: 'road:wheel_pointer', src: wheelPointerUrl, path: 'wheel/pointer.png' },
};

// The dig turns up the cups' heap of coins, and the Wheel Run's own bomb.
ROAD_GAMES.dig = {
  ...ROAD_GAMES.dig,
  coins: { alias: 'road:dig_coins', src: cupsCoinsUrl, path: 'minigame/cups/coins.png' },
  bomb: { alias: 'road:dig_bomb', src: bombUrl, path: 'item/bomb.png' },
};

// Whack's prize ladder sits in the side lights' brass bezels.
ROAD_GAMES.whack = {
  ...ROAD_GAMES.whack,
  bezel: { alias: 'road:whack_bezel', src: lightBezelUrl, path: 'road/light_bezel.png' },
};

/**
 * A world's own pieces for a game (assets/minigame/<kind>/<theme>/<name>.png),
 * by folder and then by kind, as `road:<kind>_<name>@<folder>`: sand to dig
 * on the cove, snow moles on Frost. The folder is the world's road slot, so
 * a world's pieces count for every slot in it (a slot could have a folder of
 * its own too). Each stands in for the shared piece of the same name; the
 * game screens ask for the slot's alias first, then its world's
 * (SlotDemoScene). The folder is in the alias because an alias keeps its
 * first picture.
 */
const WORLD_GAMES: Record<string, Record<string, Record<string, ThemeAssetEntry>>> = {};

for (const [file, mod] of Object.entries(WORLD_GAME_ART)) {
  const match = /minigame\/([^/]+)\/([^/]+)\/([^/]+)\.png$/.exec(file);
  if (!match) continue;
  const [, kind, theme, name] = match as unknown as [string, string, string, string];
  ((WORLD_GAMES[theme] ??= {})[kind] ??= {})[name] = {
    alias: `road:${kind}_${name}@${theme}`,
    src: mod.default,
    path: `minigame/${kind}/${theme}/${name}.png`,
  };
}

/** The road's games' art on one slot: the shared pieces, with its world's in
 *  place of any it has and the slot's own over both, so a piece stood in for
 *  isn't fetched there. */
export function roadGamesFor(themeId: string): Record<string, Record<string, ThemeAssetEntry>> {
  const world = worldOf(themeId);
  const layers = [world !== themeId ? WORLD_GAMES[world] : undefined, WORLD_GAMES[themeId]].filter(
    (l): l is Record<string, Record<string, ThemeAssetEntry>> => l !== undefined,
  );
  if (layers.length === 0) return ROAD_GAMES;
  const games = { ...ROAD_GAMES };
  for (const layer of layers) {
    for (const [kind, pieces] of Object.entries(layer)) games[kind] = { ...games[kind], ...pieces };
  }
  return games;
}

/** The painted plates BrassButton draws its buttons on (gold for the
 *  action, wood otherwise); it draws its own until they load. */
export const BUTTON_PLATES: Record<string, ThemeAssetEntry> = {
  plate_gold: { alias: 'shared:button:plate_gold', src: plateGoldUrl, path: 'ui_button/plate_gold.png' },
  plate_wood: { alias: 'shared:button:plate_wood', src: plateWoodUrl, path: 'ui_button/plate_wood.png' },
};
